import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#000000', label: 'Black' },
    { value: '#1a237e', label: 'Dark Blue' },
    { value: '#2e7d32', label: 'Dark Green' },
    { value: '#d84315', label: 'Dark Orange' },
    { value: '#6a1b9a', label: 'Purple' }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Full Stack Developer",
  phone: "+91 9876543210",
  email: "johndoe@email.com",
  city: "Chennai, India",
  website: "www.johndoe.com",
  profile:
    "Highly motivated Full Stack Developer with experience in building scalable MERN stack applications. Passionate about problem solving and creating user-friendly solutions.",
};

const defaultLeftSectionOrder = [
  'contact',
  'education',
  'languages',
  'skills',
  'references'
];

const defaultRightSectionOrder = [
  'profile',
  'workExperience',
  'certifications',
  'accomplishments',
  'volunteering',
  'interests',
  'additionalInfo'
];

const Resume35 = ({
  color = "#000000",
  font = "Arial, sans-serif",
  fontSize = "11px",
  headingSize = "14px",
  fontStyle = "normal",
  sectionSpacing = 10,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Global font style
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    ...additionalStyles
  });

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
    setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
    setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
    setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
    setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
    setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
    setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
    setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
    setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const items = (parsedData.items || []).filter(item => item && item.trim() !== '');
            if (items.length > 0) sectionsData[sectionName] = items;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }

    setCustomSections(sectionsData);

    const savedLeft = localStorage.getItem("resume35LeftSectionOrder");
    const savedRight = localStorage.getItem("resume35RightSectionOrder");

    if (savedLeft && savedRight) {
      setLeftSectionOrder(JSON.parse(savedLeft));
      setRightSectionOrder(JSON.parse(savedRight));
    } else {
      const customIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    }
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0) localStorage.setItem("resume35LeftSectionOrder", JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) localStorage.setItem("resume35RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const currentCustomIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filtered = rightSectionOrder.filter(s => !s.startsWith('custom_'));
      const newRight = [...filtered, ...currentCustomIds];
      if (JSON.stringify(newRight) !== JSON.stringify(rightSectionOrder)) {
        setRightSectionOrder(newRight);
        localStorage.setItem("resume35RightSectionOrder", JSON.stringify(newRight));
      }
    }
  }, [customSections]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { index: sourceIndex, side: sourceSide, sectionId } = dropData;

    if (sourceSide === 'left' && sourceIndex !== targetIndex) {
      const newOrder = [...leftSectionOrder];
      const [moved] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, moved);
      setLeftSectionOrder(newOrder);
    } else if (sourceSide === 'right') {
      const newLeft = [...leftSectionOrder];
      const newRight = [...rightSectionOrder];
      newRight.splice(sourceIndex, 1);
      newLeft.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeft);
      setRightSectionOrder(newRight);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { index: sourceIndex, side: sourceSide, sectionId } = dropData;

    if (sourceSide === 'right' && sourceIndex !== targetIndex) {
      const newOrder = [...rightSectionOrder];
      const [moved] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, moved);
      setRightSectionOrder(newOrder);
    } else if (sourceSide === 'left') {
      const newLeft = [...leftSectionOrder];
      const newRight = [...rightSectionOrder];
      newLeft.splice(sourceIndex, 1);
      newRight.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeft);
      setRightSectionOrder(newRight);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => {
    const customIds = Object.keys(customSections).map(name => `custom_${name}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
  };
  const resetAllSectionOrder = () => {
    resetLeftSectionOrder();
    resetRightSectionOrder();
  };

  // Section Header component
  const SectionHeader = ({ title }) => (
    <Box sx={{ mb: `${sectionSpacing}px`, mt: `${sectionSpacing}px` }}>
      <Typography 
        style={{ 
          fontSize: headingSize, 
          fontFamily: font, 
          fontStyle: fontStyle 
        }} 
        sx={{ 
          fontWeight: "bold", 
          color: color, 
          textTransform: "uppercase",
          letterSpacing: 0.5,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ width: "100%", height: `${lineWeight}px`, bgcolor: "#ccc", mt: 0.5 }} />
    </Box>
  );

  // Common text style with word break
  const textStyle = {
    ...globalFontStyle,
    wordBreak: "break-word",
    overflowWrap: "break-word",
  };

  const renderSidebarSections = () => {
    return leftSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'contact':
          return (data.phone || data.email || data.city || data.linkedIn) ? (
            <div key="contact" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="CONTACT" />
              <List dense sx={{ width: '100%', mb: `${sectionSpacing}px`, p: 0 }}>
                {data.phone && (
                  <ListItem sx={{ mb: `${paragraphSpacing / 3}px`, px: 0, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}>
                      <PhoneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={data.phone} primaryTypographyProps={{ ...textStyle }} />
                  </ListItem>
                )}
                {data.email && (
                  <ListItem sx={{ mb: `${paragraphSpacing / 3}px`, px: 0, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}>
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={data.email} primaryTypographyProps={{ ...textStyle, wordBreak: "break-all" }} />
                  </ListItem>
                )}
                {data.city && (
                  <ListItem sx={{ mb: `${paragraphSpacing / 3}px`, px: 0, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}>
                      <LocationOnIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={data.city} primaryTypographyProps={{ ...textStyle }} />
                  </ListItem>
                )}
                {data.linkedIn && (
                  <ListItem sx={{ mb: `${paragraphSpacing / 3}px`, px: 0, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}>
                      <LinkedInIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={data.linkedIn} primaryTypographyProps={{ ...textStyle, wordBreak: "break-all" }} />
                  </ListItem>
                )}
              </List>
            </div>
          ) : null;

        case 'education':
          return educationEntries.length > 0 ? (
            <div key="education" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="EDUCATION" />
              <Box sx={{ width: '100%', mb: `${sectionSpacing}px` }}>
                {educationEntries.map((edu) => {
                  const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                  const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                  const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
                  const location = edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation;
                  return (
                    <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px` }}>
                      <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333" }}>{degreeName}</Typography>
                      <Typography sx={{ ...textStyle, color: "#666" }}>{institutionName}</Typography>
                      <Typography sx={{ ...textStyle, color: "#666", fontStyle: 'italic' }}>
                        {formatDate(edu.gradMonth, edu.gradYear)}
                        {location && ` | ${location}`}
                        {edu.percentage && ` | ${edu.percentage}%`}
                        {edu.cgpa && ` | CGPA: ${edu.cgpa}`}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </div>
          ) : null;

        case 'languages':
          return savedLanguages.length > 0 ? (
            <div key="languages" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="LANGUAGES" />
              <Box sx={{ width: '100%', mb: `${sectionSpacing}px` }}>
                {savedLanguages.map((lang, index) => (
                  <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>
                    - {lang.name} {lang.level ? `(${lang.level})` : ""}
                  </Typography>
                ))}
              </Box>
            </div>
          ) : null;

        case 'skills':
          return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
            <div key="skills" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="SKILLS" />
              <Box sx={{ width: '100%', mb: `${sectionSpacing}px` }}>
                {savedSkills.map((skill, index) => (
                  <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>
                    {skill.name} {skill.rating ? `(${skill.rating}%)` : ""}
                  </Typography>
                ))}
                {softwareSkills.map((skill, index) => (
                  <Typography key={`sw-${index}`} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>
                    {skill.name} {skill.level ? `(${skill.level}%)` : ""}
                  </Typography>
                ))}
              </Box>
            </div>
          ) : null;

        case 'references':
          return savedReferences.length > 0 ? (
            <div key="references" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="REFERENCES" />
              <Box sx={{ width: '100%', mb: `${sectionSpacing}px` }}>
                {savedReferences.map((ref, index) => (
                  <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                    <Typography sx={{ ...textStyle, color: "#444" }}>- {ref}</Typography>
                  </Box>
                ))}
              </Box>
            </div>
          ) : null;

        default:
          return renderRightContentSectionsForSidebar(sectionId);
      }
    }).filter(Boolean);
  };

  const renderRightContentSectionsForSidebar = (sectionId) => {
    switch (sectionId) {
      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="PROFILE" />
            <Typography sx={{ ...textStyle, color: "#444", mb: 2, lineHeight: 1.5 }}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="EXPERIENCE" />
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333", mb: 0.5 }}>{work.jobTitle}</Typography>
                <Typography sx={{ ...textStyle, color: "#666", mb: 0.5, fontStyle: 'italic' }}>
                  {work.employer}{work.city ? ` | ${work.city}` : ""}
                </Typography>
                <Typography sx={{ ...textStyle, fontStyle: 'italic', color: '#666', mb: 0.5 }}>
                  {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                </Typography>
                {work.description && (
                  <Box
                    sx={{ "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: "break-word" } }}
                    dangerouslySetInnerHTML={{ __html: work.description.replace(/\n/g, "<br/>") }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="CERTIFICATIONS" />
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333" }}>{cert.name}</Typography>
                <Typography sx={{ ...textStyle, color: "#666" }}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="ACCOMPLISHMENTS" />
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>- {acc}</Typography>
            ))}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="VOLUNTEERING" />
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333" }}>{vol.subtopic}</Typography>
                <Typography sx={{ ...textStyle, color: '#666', fontStyle: 'italic' }}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={{ ...textStyle, color: "#444" }}>{vol.content}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="INTERESTS" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} sx={{ ...textStyle, color: "#444", mr: 1.5, mb: 0.5 }}>• {interest}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionHeader title="ADDITIONAL INFORMATION" />
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>• {info}</Typography>
            ))}
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const validItems = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
                <SectionHeader title={customSectionName.replace(/_/g, ' ').toUpperCase()} />
                {validItems.map((item, i) => (
                  <Typography key={i} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>- {item}</Typography>
                ))}
              </div>
            );
          }
        }
        return null;
    }
  };

  const renderRightContentSections = () => {
    return rightSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'profile':
          return (savedSummary || data.profile) ? (
            <div key="profile" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="ABOUT ME" />
              <Typography sx={{ ...textStyle, color: "#444", mb: 2, lineHeight: 1.5 }}>
                {savedSummary || data.profile}
              </Typography>
            </div>
          ) : null;

        case 'workExperience':
          return workExperiences && workExperiences.length > 0 ? (
            <div key="workExperience" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="EXPERIENCE" />
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333", mb: 0.5 }}>{work.jobTitle}</Typography>
                  <Typography sx={{ ...textStyle, color: "#666", mb: 0.5, fontStyle: 'italic' }}>
                    {work.employer}{work.city ? ` | ${work.city}` : ""}
                  </Typography>
                  <Typography sx={{ ...textStyle, fontStyle: 'italic', color: '#666', mb: 0.5 }}>
                    {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                  {work.description && (
                    <Box
                      sx={{ "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: "break-word" } }}
                      dangerouslySetInnerHTML={{ __html: work.description.replace(/\n/g, "<br/>") }}
                    />
                  )}
                </Box>
              ))}
            </div>
          ) : null;

        case 'certifications':
          return savedCertifications.length > 0 ? (
            <div key="certifications" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="CERTIFICATIONS" />
              {savedCertifications.map((cert, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333" }}>{cert.name}</Typography>
                  <Typography sx={{ ...textStyle, color: "#666" }}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
                </Box>
              ))}
            </div>
          ) : null;

        case 'accomplishments':
          return savedAccomplishments.length > 0 ? (
            <div key="accomplishments" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="ACCOMPLISHMENTS" />
              {savedAccomplishments.map((acc, index) => (
                <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>- {acc}</Typography>
              ))}
            </div>
          ) : null;

        case 'volunteering':
          return savedVolunteering.length > 0 ? (
            <div key="volunteering" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="VOLUNTEERING" />
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333" }}>{vol.subtopic}</Typography>
                  <Typography sx={{ ...textStyle, color: '#666', fontStyle: 'italic' }}>{vol.fromDate} - {vol.toDate}</Typography>
                  <Typography sx={{ ...textStyle, color: "#444" }}>{vol.content}</Typography>
                </Box>
              ))}
            </div>
          ) : null;

        case 'interests':
          return savedInterests.length > 0 ? (
            <div key="interests" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="INTERESTS" />
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {savedInterests.map((interest, index) => (
                  <Typography key={index} sx={{ ...textStyle, color: "#444", mr: 1.5, mb: 0.5 }}>• {interest}</Typography>
                ))}
              </Box>
            </div>
          ) : null;

        case 'additionalInfo':
          return savedAdditionalInfo.length > 0 ? (
            <div key="additionalInfo" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionHeader title="ADDITIONAL INFORMATION" />
              {savedAdditionalInfo.map((info, index) => (
                <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>• {info}</Typography>
              ))}
            </div>
          ) : null;

        default:
          return renderLeftSectionsForRight(sectionId);
      }
    }).filter(Boolean);
  };

  const renderLeftSectionsForRight = (sectionId) => {
    switch (sectionId) {
      case 'contact':
        return (data.phone || data.email || data.city || data.linkedIn) ? (
          <div key="contact" className="resume-section" style={{ ...globalFontStyle }}>
            <SectionHeader title="CONTACT" />
            <List dense sx={{ p: 0, mb: `${sectionSpacing}px` }}>
              {data.phone && (
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}><PhoneIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={data.phone} primaryTypographyProps={{ ...textStyle }} />
                </ListItem>
              )}
              {data.email && (
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}><EmailIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={data.email} primaryTypographyProps={{ ...textStyle, wordBreak: "break-all" }} />
                </ListItem>
              )}
              {data.city && (
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}><LocationOnIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={data.city} primaryTypographyProps={{ ...textStyle }} />
                </ListItem>
              )}
              {data.linkedIn && (
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ color: color, minWidth: "28px", mt: 0.3 }}><LinkedInIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={data.linkedIn} primaryTypographyProps={{ ...textStyle, wordBreak: "break-all" }} />
                </ListItem>
              )}
            </List>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className="resume-section" style={{ ...globalFontStyle }}>
            <SectionHeader title="EDUCATION" />
            <Box sx={{ mb: `${sectionSpacing}px` }}>
              {educationEntries.map((edu) => {
                const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                return (
                  <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px` }}>
                    <Typography sx={{ ...textStyle, fontWeight: "bold", color: "#333" }}>{degreeName}</Typography>
                    <Typography sx={{ ...textStyle, color: "#666" }}>{institutionName}</Typography>
                    <Typography sx={{ ...textStyle, color: "#666", fontStyle: 'italic' }}>{formatDate(edu.gradMonth, edu.gradYear)}</Typography>
                  </Box>
                );
              })}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className="resume-section" style={{ ...globalFontStyle }}>
            <SectionHeader title="LANGUAGES" />
            <Box sx={{ mb: `${sectionSpacing}px` }}>
              {savedLanguages.map((lang, index) => (
                <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>- {lang.name} {lang.level ? `(${lang.level})` : ""}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className="resume-section" style={{ ...globalFontStyle }}>
            <SectionHeader title="SKILLS" />
            <Box sx={{ mb: `${sectionSpacing}px` }}>
              {savedSkills.map((skill, index) => (
                <Typography key={index} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>{skill.name} {skill.rating ? `(${skill.rating}%)` : ""}</Typography>
              ))}
              {softwareSkills.map((skill, index) => (
                <Typography key={`sw-${index}`} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>{skill.name} {skill.level ? `(${skill.level}%)` : ""}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className="resume-section" style={{ ...globalFontStyle }}>
            <SectionHeader title="REFERENCES" />
            <Box sx={{ mb: `${sectionSpacing}px` }}>
              {savedReferences.map((ref, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ ...textStyle, color: "#444" }}>- {ref}</Typography>
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const validItems = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
                <SectionHeader title={customSectionName.replace(/_/g, ' ').toUpperCase()} />
                {validItems.map((item, i) => (
                  <Typography key={i} sx={{ ...textStyle, color: "#444", mb: 0.5 }}>- {item}</Typography>
                ))}
              </div>
            );
          }
        }
        return null;
    }
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'contact': 'Contact', 'education': 'Education', 'skills': 'Skills',
      'languages': 'Languages', 'profile': 'Profile', 'workExperience': 'Work Experience',
      'certifications': 'Certifications', 'accomplishments': 'Accomplishments',
      'volunteering': 'Volunteering', 'interests': 'Interests',
      'additionalInfo': 'Additional Information', 'references': 'References'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections ({sections.length})
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  // Pagination for sidebar content
  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(sidebarContentRef.current.querySelectorAll('.sidebar-section'));
    
    let newSidebarPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
    });
    
    if (newSidebarPages.length === 0) newSidebarPages.push([]);
    setSidebarPages(newSidebarPages);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, customSections]);

  // Pagination for main content
  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(rightContentRef.current.querySelectorAll('.resume-section'));
    
    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newPages.push([...currentPageSections]);
    });
    
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [paginateSidebarContent, paginateMainContent, educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, customSections, leftSectionOrder, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color]);

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    return htmlContent.replace(/<(div|p|span)([^>]*)/g, (match, tagName, attrs) => {
      const styleRegex = /style="([^"]*)"/;
      const styleMatch = attrs.match(styleRegex);
      const defaultStyles = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word;`;
      if (styleMatch) {
        const existingStyle = styleMatch[1];
        const needsFontSize = !existingStyle.includes('font-size:');
        const fontSizeStyle = needsFontSize ? `font-size: ${fontSize};` : '';
        const mergedStyle = `style="${defaultStyles} ${fontSizeStyle} ${existingStyle}"`;
        return `<${tagName}${attrs.replace(styleRegex, mergedStyle)}`;
      } else {
        return `<${tagName}${attrs} style="${defaultStyles} font-size: ${fontSize};"`;
      }
    });
  };

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  // Common page styles for the paper
  const pageContainerStyle = {
    width: page.width,
    minHeight: page.height,
    mx: "auto",
    bgcolor: "#fff",
    ...globalFontStyle,
    display: "flex",
    flexDirection: "column",
    pageBreakAfter: "always",
    boxSizing: "border-box",
    overflow: "hidden",
    "@media print": {
      width: page.width, height: "auto", minHeight: page.height,
      boxShadow: "none", m: 0, ...globalFontStyle
    },
  };

  return (
    <Box sx={{ width: "100%", position: "relative", m: exporting ? 0 : 5 }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {/* Hidden measurement containers - MUST be fixed width matching column width */}
      <Box ref={sidebarContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -9999, left: 0, width: "25%", ...globalFontStyle }}>
        {renderSidebarSections()}
      </Box>
      <Box ref={rightContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -9999, left: 0, width: "65%", ...globalFontStyle }}>
        {renderRightContentSections()}
      </Box>
      
      {/* Visible resume content */}
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              ...pageContainerStyle,
              boxShadow: pageIndex === currentPage ? 3 : 0,
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              '@media print': { display: 'flex' }
            }}
          >
            <Box sx={{ 
              width: "100%",
              height: "100%",
              p: `${topBottomMargin}px ${sideMargins}px`,
              flex: 1,
              pt: pageIndex > 0 ? `${sectionSpacing}px` : `${topBottomMargin}px`,
              boxSizing: "border-box",
              overflow: "hidden",
              ...globalFontStyle
            }}>
              {/* Name and Title Section - Only on first page */}
              {pageIndex === 0 && (
                <Box sx={{ mb: 3, textAlign: "left", overflow: "hidden" }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: "bold", color: color, mb: 0.5,
                    fontSize: `calc(${headingSize} * 1.8)`, letterSpacing: 0.5, textTransform: "uppercase",
                    fontFamily: font, wordBreak: "break-word",
                  }}>
                    {data.firstName} {data.lastName}
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    color: "#666", fontWeight: 500, mb: 2, fontSize: `calc(${headingSize} * 1.1)`,
                    letterSpacing: 1, fontFamily: font, fontStyle: fontStyle, wordBreak: "break-word",
                  }}>
                    {data.currentPosition}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={3} sx={{ flexWrap: "nowrap" }}>
                {/* Left Column - fixed 25% */}
                <Grid item sx={{ width: "25%", flexShrink: 0, minWidth: 0, overflow: "hidden" }}>
                  {sidebarPages[pageIndex] ? (
                    <Box
                      sx={{ wordBreak: "break-word", overflowWrap: "break-word", overflow: "hidden", ...globalFontStyle }}
                      dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(sidebarPages[pageIndex].join('')) }}
                    />
                  ) : pageIndex === 0 && (
                    <Typography sx={{ ...textStyle }}>Loading...</Typography>
                  )}
                </Grid>

                {/* Right Column - 65% with vertical separator */}
                <Grid item sx={{ width: "65%", flexShrink: 0, minWidth: 0, overflow: "hidden" }}>
                  <Box sx={{ position: "relative", pl: 3, height: "100%" }}>
                    {/* Vertical separator */}
                    <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "1px", bgcolor: "#ddd" }} />
                    {pages[pageIndex] ? (
                      <Box
                        sx={{ wordBreak: "break-word", overflowWrap: "break-word", overflow: "hidden", ...globalFontStyle }}
                        dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }}
                      />
                    ) : pageIndex === 0 && (
                      <Typography sx={{ ...textStyle }}>No content for this page</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Contact footer on last page */}
              {pageIndex === totalPages - 1 && (data.phone || data.email || data.city || data.linkedIn) && (
                <Box sx={{ mt: 2, borderTop: "1px solid #ccc", pt: 1 }}>
                  <Typography sx={{ fontSize: "10px", color: "#666", textAlign: "center", fontFamily: font, fontStyle: fontStyle, wordBreak: "break-word" }}>
                    {data.phone && `Phone: ${data.phone}`}
                    {data.email && ` | Email: ${data.email}`}
                    {data.city && ` | Address: ${data.city}`}
                    {data.linkedIn && ` | LinkedIn: ${data.linkedIn}`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder or move between left sidebar and right content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1 }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume35;